import React from 'react';
import { Box, MenuItem } from '@mui/material';
import { CalendarDateService, DateObject } from '@uplift/services';
import { SelectRaw } from '@uplift/ui-v2';


export const YEAR_VALUE_FORMAT = 'YYYY';
export const MONTH_VALUE_FORMAT = 'YYYY-MM';


export type MonthPickerProps = {
  label: string;
  minDate: DateObject;
  maxDate: DateObject;
  value: DateObject;
  onChange: (value: DateObject) => void;
};


export const MonthPicker: React.FC<MonthPickerProps> = ({ label, minDate, maxDate, value, onChange }) => {
  const [ year, setYear ] = React.useState(value?.format(YEAR_VALUE_FORMAT));
  const month = React.useMemo(() => value?.format(MONTH_VALUE_FORMAT), [ value ]);

  const years = React.useMemo(() => {
    const items = [];

    if (!minDate || !maxDate) {
      return items;
    }

    for (let i = minDate; i.year() <= maxDate.year(); i = i.add(1, 'year')) {
      items.push(i.format(YEAR_VALUE_FORMAT));
    }

    return items;
  }, [ minDate, maxDate ]);

  const months = React.useMemo(() => {
    const result = {};

    if (!minDate || !maxDate) {
      return result;
    }

    for (let i = minDate; i.isBefore(maxDate) || i.isSame(maxDate); i = i.add(1, 'month')) {
      const year = i.year().toString();

      if (!result[year]) {
        result[year] = [];
      }
      result[year].push({
        value: i.format(MONTH_VALUE_FORMAT),
        label: i.format('MMMM'),
      });
    }

    return result;
  }, [ minDate, maxDate ]);

  const handleChange = (newMonth: string) => {
    const newValue = CalendarDateService.date(newMonth, MONTH_VALUE_FORMAT);

    onChange?.(newValue);
  };

  const onChangeMonth = (e: React.ChangeEvent<{ name?: string; value: string }>) => {
    handleChange(e.target.value);
  };

  const onChangeYear = (e: React.ChangeEvent<{ name?: string; value: string }>) => {
    const selectedYear = e.target.value;
    const lastIndexForYearSelect = parseInt(year) > parseInt(selectedYear);
    const index = lastIndexForYearSelect ? (months[selectedYear].length - 1) : 0;
    const month = months[selectedYear][index];

    setYear(selectedYear);
    handleChange(month.value);
  };

  React.useEffect(() => {
    setYear(value?.format(YEAR_VALUE_FORMAT));
  }, [ value ]);

  return (
    <Box display="flex" alignItems="center">
      <Box mr={2} width="40px">{label}</Box>
      <Box display="flex" width="100%">
        <Box display="flex" flex={3} mr={2}>
          <SelectRaw name="months" value={month} onChange={onChangeMonth} fullWidth>
            {months?.[year]?.map(item =>
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            )}
          </SelectRaw>
        </Box>
        <Box display="flex" flex={2}>
          <SelectRaw name="years" value={year} onChange={onChangeYear} fullWidth>
            {years?.map(item =>
              <MenuItem key={item} value={item}>{item}</MenuItem>
            )}
          </SelectRaw>
        </Box>
      </Box>
    </Box>
  );
};
